import React, { useState, useEffect } from 'react';
import { format, isValid, parseISO, isPast, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  IconButton, 
  Box, 
  InputAdornment, 
  CircularProgress, 
  Grid, 
  Tooltip, 
  Typography,
  MenuItem,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Button,
  SelectChangeEvent,
  Paper,
  TextField,
  Dialog,
  DialogContentText
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import CloseIcon from '@mui/icons-material/Close';
import TitleIcon from '@mui/icons-material/Title';
import DescriptionIcon from '@mui/icons-material/Description';
import LabelIcon from '@mui/icons-material/Label';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import PersonIcon from '@mui/icons-material/Person';
import AttachmentIcon from '@mui/icons-material/Attachment';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MovingIcon from '@mui/icons-material/Moving';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import {
  StyledDialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogActions,
  TabsContainer,
  TabsList,
  Tab,
  FormGroup,
  FormLabel,
  StyledTextField,
  OptionsRow,
  OptionCard,
  OptionIcon,
  OptionTitle,
  DatePickerField,
  CategoryChip,
  CancelButton,
  SaveButton,
  DeleteButton,
  AttachmentsList,
  CommentsList,
  CommentItem,
  CommentHeader,
  CommentAuthor,
  CommentDate,
  CommentText,
  AttachmentItem,
  AttachmentInfo,
  AttachmentName,
  AttachmentSize,
  FileUploadContainer,
} from './styles';

import { Card, CardLabel } from '../../interfaces/board';
import { Priority, PriorityType, Status, StatusType } from '../../enums';
import { getStatusLabel, getStatusColor } from '../../enums/Status';

import { projectService } from '../../services/project';
import api from '../../services/api';

interface OpenCardModalProps {
  open: boolean;
  onClose: () => void;
  cardId: string;
  columnId: string;
  onCardUpdate?: (updatedCard: Card) => void;
  isObserver?: boolean;
}

const priorityOptions = [
  { value: Priority.LOW, label: 'Baixa', color: '#4caf50' },
  { value: Priority.MEDIUM, label: 'Média', color: '#ff9800' },
  { value: Priority.HIGH, label: 'Alta', color: '#f44336' }
];

const statusOptions = [
  { value: Status.TODO, label: 'A Fazer', color: '#6c757d' },
  { value: Status.IN_PROGRESS, label: 'Em Progresso', color: '#3498db' },
  { value: Status.REVIEW, label: 'Em Revisão', color: '#f39c12' },
  { value: Status.DONE, label: 'Concluído', color: '#2ecc71' },
  { value: Status.BLOCKED, label: 'Bloqueado', color: '#e74c3c' }
];

const labelOptions = [
  { id: 'label1', name: 'Bug', color: '#f44336' },
  { id: 'label2', name: 'Documentação', color: '#2196f3' },
  { id: 'label3', name: 'Melhoria', color: '#4caf50' },
  { id: 'label4', name: 'Novo recurso', color: '#9c27b0' },
  { id: 'label5', name: 'Tarefa', color: '#ff9800' },
  { id: 'label6', name: 'Pesquisa', color: '#03a9f4' }
];

interface User {
  id: string;
  name: string;
  avatar?: string;
  role?: string;
}

interface Attachment {
  id: string;
  fileName: string;
  fileSize: number;
  contentType: string;
  createdAt: string;
  userId: string;
  userName: string;
}

interface Comment {
  id: string;
  content: string;
  createdAt: string;
  userId: string;
  userName: string;
  userAvatar?: string;
}

export const OpenCardModal: React.FC<OpenCardModalProps> = ({ 
  open, 
  onClose, 
  cardId,
  columnId,
  onCardUpdate,
  isObserver = false
}) => {
  const [isProjectAdmin, setIsProjectAdmin] = useState(false);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [canDeleteCard, setCanDeleteCard] = useState(false);
  const [dueDateWasSet, setDueDateWasSet] = useState(false);
  const [activeTab, setActiveTab] = useState('main');
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<PriorityType | undefined>(undefined);
  const [status, setStatus] = useState<StatusType | undefined>(undefined);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);
  
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  const [users, setUsers] = useState<User[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  
  const [originalCard, setOriginalCard] = useState<Card | null>(null);
  
  const [hasChanges, setHasChanges] = useState(false);
  
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [deleteCardConfirmOpen, setDeleteCardConfirmOpen] = useState(false);

  useEffect(() => {
    if (open && cardId) {
      fetchCardDetails();
      fetchUsers();
    }
  }, [open, cardId]);
  
  useEffect(() => {
    if (originalCard) {
      const isSameDueDate = () => {
        if (!dueDate && !originalCard.dueDate) return true;
        if (!dueDate || !originalCard.dueDate) return false;
        
        try {
          const origDate = parseISO(originalCard.dueDate);
          
          const dueDateYear = dueDate.getFullYear();
          const dueDateMonth = dueDate.getMonth();
          const dueDateDay = dueDate.getDate();
          
          const origDateYear = origDate.getFullYear();
          const origDateMonth = origDate.getMonth();
          const origDateDay = origDate.getDate();
          
          return (
            dueDateYear === origDateYear &&
            dueDateMonth === origDateMonth &&
            dueDateDay === origDateDay
          );
        } catch (e) {
          return false;
        }
      };
      
      const hasUnsavedChanges = 
        title !== originalCard.title ||
        description !== (originalCard.description || '') ||
        priority !== originalCard.priority ||
        status !== originalCard.status ||
        !areArraysEqual(selectedAssignees, originalCard.assignees?.map(a => a.id) || []) ||
        !isSameDueDate();
      
      setHasChanges(hasUnsavedChanges);
      
      if (currentUserId && originalCard.creatorId) {
        // Um usuário pode excluir o card se:
        // 1. For administrador do sistema (Admin/Developer)
        // 2. For administrador do projeto
        // 3. For o criador do card
        const isCreatorOfCard = currentUserId === originalCard.creatorId;
        setCanDeleteCard(isSystemAdmin || isProjectAdmin || isCreatorOfCard);
      } else {
        // Se não temos informação do criador, apenas admins (sistema ou projeto) podem excluir
        setCanDeleteCard(isSystemAdmin || isProjectAdmin);
      }
    }
  }, [title, description, priority, status, dueDate, selectedAssignees, originalCard, isProjectAdmin, isSystemAdmin, currentUserId]);
  
  const areArraysEqual = (arr1: any[], arr2: any[]): boolean => {
    if (arr1.length !== arr2.length) return false;
    const sortedArr1 = [...arr1].sort();
    const sortedArr2 = [...arr2].sort();
    return sortedArr1.every((value, index) => value === sortedArr2[index]);
  };

  const fetchCardDetails = async () => {
    setLoading(true);
    try {
      try {
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = currentUser.id;
        const userRole = currentUser.role;
        setCurrentUserId(userId);
        setCurrentUserRole(userRole);
        
        // Verificar se é administrador do sistema
        const isSystemAdminUser = userRole && (
          userRole.toLowerCase() === 'admin' ||
          userRole.toLowerCase() === 'administrator' ||
          userRole.toLowerCase() === 'developer' ||
          userRole.toLowerCase() === 'dev'
        );
        setIsSystemAdmin(isSystemAdminUser);
      } catch (err) {
        setCurrentUserId(null);
        setCurrentUserRole(null);
        setIsSystemAdmin(false);
      }

      const cardResponse = await projectService.getCardDetails(cardId);
      
      if (cardResponse) {
        setTitle(cardResponse.title);
        setDescription(cardResponse.description || '');
        
        try {
          if (typeof localStorage !== 'undefined') {
            const savedPrioritiesStr = localStorage.getItem('cardPriorities');
            if (savedPrioritiesStr) {
              const cardPriorities = JSON.parse(savedPrioritiesStr);
              if (cardPriorities[cardId]) {
                setPriority(cardPriorities[cardId]);
              } else {
                setPriority(cardResponse.priority);
              }
            } else {
              setPriority(cardResponse.priority);
            }
          } else {
            setPriority(cardResponse.priority);
          }
        } catch (e) {
          setPriority(cardResponse.priority);
        }
        
        setStatus(cardResponse.status);
        
        if (cardResponse.dueDate) {
          const parsedDate = new Date(cardResponse.dueDate);
          setDueDate(parsedDate);
          setDueDateWasSet(true);
        } else {
          setDueDate(null);
          setDueDateWasSet(false);
        }
        setSelectedAssignees(cardResponse.assignees?.map(assignee => assignee.id) || []);
        setSelectedLabels(cardResponse.labels?.map(label => label.id) || []);
        
        setComments(cardResponse.comments || []);
        setAttachments(cardResponse.attachments || []);
        
        setOriginalCard(cardResponse);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const columnResponse = await api.get(`/column-boards/${columnId}`);
      const boardId = columnResponse.data.data.boardId;
      
      const boardResponse = await api.get(`/boards/${boardId}`);
      const projectId = boardResponse.data.data.projectId;
      
      // Buscar usuários disponíveis para o board usando o novo método do serviço
      const availableUsers = await projectService.getBoardAvailableAssignees(boardId);
      
      const formattedUsers = availableUsers.map((user: any) => ({
        id: user.id,
        name: user.firstName && user.lastName 
          ? `${user.firstName} ${user.lastName}` 
          : user.username || user.email,
        avatar: user.avatarUrl,
        role: user.isAdmin ? 'Administrador' : 
              user.isBoardMember ? 'Membro do Board' : 
              user.isObserver ? 'Observador' : 'Membro'
      }));
      
      setUsers(formattedUsers);
      
      try {
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const currentUserId = currentUser.id;
        
        // Verificar se o usuário é administrador do projeto
        const projectResponse = await api.get(`/projects/${projectId}/members`);
        const isAdmin = projectResponse.data.data.some(
          (user: any) => user.id === currentUserId && user.isAdmin === true
        );
        
        setIsProjectAdmin(isAdmin);
      } catch (err) {
        setIsProjectAdmin(false);
        setCanDeleteCard(false);
      }
    } catch (error) {
      setUsers([]);
      setIsProjectAdmin(false);
      setCanDeleteCard(false);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSaveCard = async () => {
    if (isSaving || !hasChanges) return;

    setIsSaving(true);
    
    try {
      if (dueDateWasSet && !isProjectAdmin && 
          originalCard?.dueDate && dueDate && 
          new Date(originalCard.dueDate).toDateString() !== dueDate.toDateString()) {
        alert('Apenas administradores do projeto podem modificar a data de entrega após ela ter sido definida.');
        setIsSaving(false);
        if (originalCard?.dueDate) {
          setDueDate(new Date(originalCard.dueDate));
        }
        return;
      }
      
      let formattedDueDate;
      if (dueDate && isValid(dueDate)) {
        try {
          formattedDueDate = dueDate.toISOString();
        } catch (e) {
          formattedDueDate = undefined;
        }
      }
      
      const cardData = {
        id: cardId,
        title: title.trim() || 'Sem Título',
        description: description || '',
        priority: priority || 'low',
        status: status || 'Todo',
        dueDate: formattedDueDate,
        assignees: selectedAssignees.map(id => ({
          id,
          name: users.find(user => user.id === id)?.name || '',
          avatar: users.find(user => user.id === id)?.avatar
        }))
      };
      
      const updatedCard = await projectService.updateCard(cardId, cardData);
      
      if (priority !== originalCard?.priority && typeof localStorage !== 'undefined') {
        try {
          const savedPrioritiesStr = localStorage.getItem('cardPriorities') || '{}';
          const cardPriorities = JSON.parse(savedPrioritiesStr);
          cardPriorities[cardId] = priority;
          localStorage.setItem('cardPriorities', JSON.stringify(cardPriorities));
        } catch (e) {
        }
      }
      
      setOriginalCard(updatedCard);
      setHasChanges(false);
      
      if (!dueDateWasSet && dueDate) {
        setDueDateWasSet(true);
      }
      
      if (onCardUpdate) {
        onCardUpdate(updatedCard);
      }
      
    } catch (error: any) {
      
      if (error.response && error.response.data && 
          error.response.data.message && 
          error.response.data.message.includes('Apenas administradores')) {
        alert('Apenas administradores do projeto podem modificar a data de entrega após ela ter sido definida.');
        
        if (originalCard?.dueDate) {
          setDueDate(new Date(originalCard.dueDate));
        }
      } else {
        alert(`Erro ao atualizar o cartão: ${error.message || 'Erro desconhecido'}`);
      }
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleAddComment = async () => {
    if (!newComment.trim() || isSubmittingComment) return;
    
    setIsSubmittingComment(true);
    
    try {
      const addedComment = await projectService.addComment(cardId, newComment);
      
      setComments(prev => [addedComment, ...prev]);
      
      setNewComment('');
    } catch (error) {
    } finally {
      setIsSubmittingComment(false);
    }
  };
  
  const handleFileUpload = async () => {
    if (!selectedFile || isUploadingFile) return;
    
    setIsUploadingFile(true);
    
    try {
      const uploadedFile = await projectService.uploadAttachment(cardId, selectedFile);
      
      setAttachments(prev => [uploadedFile, ...prev]);
      
      setSelectedFile(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      alert(`Erro ao fazer upload do arquivo: ${errorMessage}`);
    } finally {
      setIsUploadingFile(false);
    }
  };
  
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };
  
  const handleDownloadAttachment = async (attachmentId: string, fileName: string) => {
    try {
      const fileBlob = await projectService.downloadAttachment(attachmentId);
      
      const blobType = fileBlob.type || 'application/octet-stream';
      const blob = new Blob([fileBlob], { type: blobType });
      
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      
      a.click();
      
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 200);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      alert(`Erro ao baixar o arquivo: ${errorMessage}`);
    }
  };
  
  const handleDeleteAttachment = async (attachmentId: string) => {
    try {
      await projectService.deleteAttachment(attachmentId);
      
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch (error) {
    }
  };
  
  const handleDeleteComment = async (commentId: string) => {
    try {
      await projectService.deleteComment(commentId);
      
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (error) {
    }
  };
  
  const handleDeleteCardClick = () => {
    // Verificar se o usuário atual é admin do sistema, admin do projeto ou criador do card
    if (!isSystemAdmin && !isProjectAdmin && originalCard?.creatorId && currentUserId !== originalCard.creatorId) {
      alert('Apenas administradores ou o criador do cartão podem excluí-lo.');
      return;
    }
    setDeleteCardConfirmOpen(true);
  };
  
  const handleDeleteCardConfirmClose = () => {
    setDeleteCardConfirmOpen(false);
  };
  
  const handleDeleteCard = async () => {
    if (isDeleting) return;
    
    // Verificar se o usuário atual é admin do sistema, admin do projeto ou criador do card
    if (!isSystemAdmin && !isProjectAdmin && originalCard?.creatorId && currentUserId !== originalCard.creatorId) {
      alert('Apenas administradores ou o criador do cartão podem excluí-lo.');
      setDeleteCardConfirmOpen(false);
      return;
    }
    
    setIsDeleting(true);
    setDeleteCardConfirmOpen(false);
    
    try {
      await projectService.deleteCard(cardId);
      
      if (onCardUpdate) {
        onCardUpdate({
          ...originalCard!,
          id: cardId,
          _deleted: true
        } as any);
      }
      
      onClose();
    } catch (error) {
      alert(`Erro ao excluir o cartão: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      
      setIsDeleting(false);
    }
  };
  
  const handleRemoveAssignee = (userId: string) => {
    if (isObserver) return;
    
    setSelectedAssignees(prev => prev.filter(id => id !== userId));
    setHasChanges(true);
  };
  
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'Data não disponível';
    
    try {
      const date = new Date(dateString);
      
      if (!isValid(date)) return 'Data inválida';
      
      return format(date, "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
    } catch (error) {
      return 'Erro ao formatar data';
    }
  };

  const formatDateNoHours = (dateString: string | undefined): string => {
    if (!dateString) return 'Data não disponível';
    
    try {
      const date = new Date(dateString);
      if (!isValid(date)) return 'Data inválida';

      return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    } catch (error) {
      return 'Erro ao formatar data';
    }
  };
  
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  const isDatePast = (date: Date | null): boolean => {
    if (!date) return false;
    
    const today = new Date();
    const todayWithoutTime = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dateWithoutTime = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    return dateWithoutTime < todayWithoutTime;
  };

  const SelectedAssigneesDisplay = () => {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 1,
        mb: 2,
        mt: 2,
        maxHeight: '120px',
        overflowY: 'auto'
      }}>
        {selectedAssignees.length > 0 ? (
          selectedAssignees.map(userId => {
            const user = users.find(u => u.id === userId);
            if (!user) return null;
            
            return (
              <Paper 
                key={userId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 10px',
                  gap: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: '4px'
                }}
              >
                <Avatar 
                  src={user.avatar} 
                  sx={{ width: 24, height: 24 }}
                >
                  {user.name.charAt(0)}
                </Avatar>
                <Typography variant="body2">{user.name}</Typography>
                <IconButton
                  onClick={() => handleRemoveAssignee(userId)}
                  size="small"
                  disabled={isObserver}
                  sx={{ 
                    padding: '2px',
                    marginLeft: '4px',
                    '&:hover': !isObserver ? { 
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    } : {},
                    opacity: isObserver ? 0.5 : 1
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Paper>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Nenhum responsável selecionado
          </Typography>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <StyledDialog
        open={open}
        onClose={onClose}
        maxWidth="md"
      >
        <DialogHeader>
          <DialogTitle>Carregando detalhes do cartão...</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'white',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogHeader>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </StyledDialog>
    );
  }
  
  return (
    <>
      <StyledDialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        aria-labelledby="view-card-dialog-title"
      >
      <DialogHeader>
        <DialogTitle id="view-card-dialog-title">
          Detalhes do Cartão
          {isObserver && (
            <Box 
              sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                bgcolor: 'rgba(25, 118, 210, 0.15)', 
                color: '#90CAF9',
                borderRadius: '4px',
                padding: '2px 8px',
                ml: 2,
                fontSize: '0.75rem',
                fontWeight: 'normal'
              }}
            >
              <VisibilityIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Modo somente leitura
            </Box>
          )}
          
          {!isObserver && !isSystemAdmin && !isProjectAdmin && originalCard?.creatorId && currentUserId !== originalCard.creatorId && (
            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
              Você só pode excluir cartões que você mesmo criou.
            </Typography>
          )}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: 'white',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogHeader>

      <TabsContainer>
        <TabsList
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="main" label="Principal" />
          <Tab value="activity" label="Atividade" />
        </TabsList>
      </TabsContainer>

      <DialogContent>
        {activeTab === 'main' && (
          <>
            <FormGroup>
              <FormLabel>Título</FormLabel>
              <StyledTextField
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TitleIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                    </InputAdornment>
                  ),
                  readOnly: isObserver
                }}
                disabled={isObserver}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Status</FormLabel>
              <OptionsRow>
                {statusOptions.map((option) => (
                  <Tooltip key={option.value} title={option.label} arrow>
                    <OptionCard
                      key={option.value}
                      selected={status === option.value}
                      onClick={isObserver ? undefined : () => {
                        setStatus(option.value);
                      }}
                      sx={{ 
                        cursor: isObserver ? 'default' : 'pointer', 
                        opacity: isObserver ? 0.7 : 1,
                        borderLeft: `5px solid ${option.color}`,
                        backgroundColor: status === option.value ? `${option.color}20` : undefined,
                        transition: 'all 0.2s ease',
                        '&:hover': isObserver ? undefined : {
                          backgroundColor: `${option.color}30`,
                          transform: 'translateY(-2px)',
                        }
                      }}
                    >
                      <OptionIcon>
                        <Box 
                          sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            backgroundColor: option.color,
                            marginRight: 1
                          }} 
                        />
                      </OptionIcon>
                      <OptionTitle>{option.label}</OptionTitle>
                    </OptionCard>
                  </Tooltip>
                ))}
              </OptionsRow>
            </FormGroup>
            
            <FormGroup>
              <FormLabel>Prioridade</FormLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                {priorityOptions.map((option) => (
                  <Tooltip key={option.value} title={option.label} arrow>
                    <Box 
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        opacity: isObserver ? 0.7 : 1,
                        cursor: isObserver ? 'default' : 'pointer',
                      }}
                      onClick={isObserver ? undefined : () => {
                        setPriority(option.value);
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: option.color,
                          border: priority === option.value ? '3px solid #fff' : '3px solid transparent',
                          boxShadow: priority === option.value ? '0 0 0 2px ' + option.color : 'none',
                          transition: 'all 0.2s ease',
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {option.label}
                      </Typography>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </FormGroup>

            <FormGroup>
              <FormLabel>Data de Entrega</FormLabel>
              <Box sx={{ position: 'relative' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <DatePicker 
                    value={dueDate}
                    onChange={(newValue) => {
                      const canEditDueDate = !isObserver && (!dueDateWasSet || isProjectAdmin);
                      if (canEditDueDate && (newValue === null || isValid(newValue))) {
                        setDueDate(newValue);
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: "DD/MM/AAAA",
                        variant: "outlined",
                        disabled: isObserver || (dueDateWasSet && !isProjectAdmin),
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <DateRangeIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                            </InputAdornment>
                          ),
                        },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            borderRadius: '6px',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&.Mui-focused fieldset': { borderColor: '#1976d2' },
                          },
                          '& .MuiInputBase-input': { color: '#fff' },
                        }
                      },
                      field: {
                        clearable: true,
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
              {dueDateWasSet && !isProjectAdmin && !isObserver && (
                <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                  Apenas administradores do projeto podem modificar a data de entrega após ela ter sido definida.
                </Typography>
              )}
              {dueDate && isValid(dueDate) && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {format(dueDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </Typography>
                  {isDatePast(dueDate) && status !== Status.DONE && (
                    <Typography variant="caption" sx={{ color: '#f44336' }}>
                      Prazo vencido
                    </Typography>
                  )}
                </Box>
              )}
            </FormGroup>

            <FormGroup>
              <FormLabel style={{ marginBottom: '10px' }}>Responsáveis</FormLabel>
              {usersLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : (
                <>
                  <FormControl fullWidth>
                    <InputLabel id="assignees-select-label">Selecione os responsáveis</InputLabel>
                    <Select
                      labelId="assignees-select-label"
                      id="assignees-select"
                      multiple
                      value={selectedAssignees}
                      onChange={(e: SelectChangeEvent<string[]>) => {
                        if (!isObserver) {
                          const value = typeof e.target.value === 'string'
                            ? e.target.value.split(',')
                            : e.target.value;
                          setSelectedAssignees(value);
                          setHasChanges(true);
                        }
                      }}
                      disabled={isObserver}
                      input={<OutlinedInput label="Selecione os responsáveis" />}
                      renderValue={() => <Typography variant="body2">Responsáveis selecionados: {selectedAssignees.length}</Typography>}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300
                          }
                        }
                      }}
                    >
                      {users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Avatar src={user.avatar} sx={{ width: 32, height: 32 }}>
                              {user.name.charAt(0)}
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography>{user.name}</Typography>
                              {user.role && (
                                <Typography variant="caption" color="text.secondary">
                                  {user.role}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <SelectedAssigneesDisplay />
                </>
              )}
            </FormGroup>

            <FormGroup>
              <FormLabel>Descrição</FormLabel>
              <StyledTextField
                fullWidth
                multiline
                rows={6}
                placeholder="Descreva os detalhes deste cartão..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isObserver}
                InputProps={{
                  readOnly: isObserver,
                  startAdornment: (
                    <InputAdornment position="start">
                      <DescriptionIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </FormGroup>

            <Divider sx={{ my: 3, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
            
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
              <InsertCommentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Comentários
            </Typography>
            
            <FormGroup>
              <FormLabel>Novo Comentário</FormLabel>
              <StyledTextField
                fullWidth
                multiline
                maxRows={2}
                rows={2}
                placeholder={isObserver ? "Observadores não podem adicionar comentários" : "Adicione um comentário..."}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                disabled={isObserver}
                inputProps={{
                  style: { maxHeight: '80px', overflowY: 'auto' },
                  readOnly: isObserver
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton 
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || isSubmittingComment || isObserver}
                        sx={{ color: '#1976d2', opacity: isObserver ? 0.5 : 1 }}
                      >
                        {isSubmittingComment ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SendIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormGroup>
            
            <Box 
              sx={{
                maxHeight: '180px',
                overflowY: 'auto',
                mt: 2,
                pr: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              {comments.length === 0 ? (
                <Box sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', py: 2 }}>
                  <Typography variant="body2">Não há comentários neste cartão</Typography>
                </Box>
              ) : (
                <CommentsList>
                  {comments.map((comment) => (
                    <CommentItem key={comment.id}>
                      <CommentHeader>
                        <Avatar 
                          src={comment.userAvatar} 
                          sx={{ width: 24, height: 24 }}
                        >
                          {comment.userName.charAt(0)}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <CommentAuthor>{comment.userName}</CommentAuthor>
                          <CommentDate>{formatDate(comment.createdAt)}</CommentDate>
                        </Box>
                        <IconButton 
                          size="small"
                          onClick={() => handleDeleteComment(comment.id)}
                          sx={{ color: 'rgba(255, 255, 255, 0.5)', display: isObserver ? 'none' : 'inline-flex' }}
                          disabled={isObserver}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </CommentHeader>
                      <CommentText>{comment.content}</CommentText>
                    </CommentItem>
                  ))}
                </CommentsList>
              )}
            </Box>

            <Divider sx={{ my: 3, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
            
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
              <AttachmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Anexos
            </Typography>
            
            <Box sx={{ mb: 2 }}>
            {!isObserver ? (
            <>
              <input
                type="file"
                id="file-upload"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
              />
            <label htmlFor="file-upload">
              <FileUploadContainer>
              <FileUploadIcon sx={{ fontSize: 40, color: 'rgba(255, 255, 255, 0.5)' }} />
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Clique para selecionar um arquivo
              </Typography>
            {selectedFile && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </Typography>
              <IconButton 
                color="primary" 
                onClick={handleFileUpload}
                  disabled={isUploadingFile}
                size="small"
            >
                {isUploadingFile ? (
                <CircularProgress size={20} />
                ) : (
                    <SendIcon fontSize="small" />
                    )}
                    </IconButton>
                    </Box>
                    )}
                    </FileUploadContainer>
                    </label>
                  </>
                ) : (
                  <Typography sx={{ 
                    color: 'rgba(255, 255, 255, 0.5)', 
                    p: 2, 
                    textAlign: 'center',
                    border: '1px dashed rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px'
                  }}>
                    <VisibilityIcon sx={{ fontSize: 20, mr: 1, verticalAlign: 'middle' }} />
                    Observadores não podem adicionar anexos
                  </Typography>
                )}
              </Box>
            
            <Box 
              sx={{
                maxHeight: '180px',
                overflowY: 'auto',
                pr: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              <AttachmentsList>
                {attachments.length === 0 ? (
                  <Box sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', py: 2 }}>
                    <Typography variant="body2">Não há anexos neste cartão</Typography>
                  </Box>
                ) : (
                  attachments.map((attachment) => (
                    <AttachmentItem key={attachment.id}>
                      <AttachmentInfo>
                        <AttachmentIcon />
                        <Box>
                          <AttachmentName>{attachment.fileName}</AttachmentName>
                          <AttachmentSize>
                            {formatFileSize(attachment.fileSize)} • Adicionado por {attachment.userName}
                          </AttachmentSize>
                        </Box>
                      </AttachmentInfo>
                      <Box>
                        <IconButton 
                          size="small"
                          onClick={() => handleDownloadAttachment(attachment.id, attachment.fileName)}
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => handleDeleteAttachment(attachment.id)}
                          sx={{ color: 'rgba(255, 255, 255, 0.5)', display: isObserver ? 'none' : 'inline-flex' }}
                          disabled={isObserver}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </AttachmentItem>
                  ))
                )}
              </AttachmentsList>
            </Box>
          </>
        )}
        
        {activeTab === 'activity' && (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontSize: '1.1rem', fontWeight: 600 }}>
              <CalendarMonthIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Histórico de Atividades
              {originalCard?.creatorId && (
                <Typography variant="caption" sx={{ display: 'block', ml: 4, mt: 1, color: 'rgba(255, 255, 255, 0.6)' }}>
                  Cartão criado por: {users.find(u => u.id === originalCard.creatorId)?.name || 'Usuário desconhecido'}
                </Typography>
              )}
            </Typography>
            
            <List sx={{ width: '100%', bgcolor: 'rgba(0, 0, 0, 0.1)', borderRadius: '8px', p: 1 }}>
              {originalCard && (
                <>
                  <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                  <MovingIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                  primary={
                    <Box>
                        <Typography fontWeight="500">Cartão criado</Typography>
                          {originalCard.creatorId && users.length > 0 && (
                        <Typography variant="caption" sx={{ color: '#90CAF9', display: 'block' }}>
                          Criado por: {users.find(u => u.id === originalCard.creatorId)?.name || 'Usuário desconhecido'}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={formatDate(originalCard.createdAt)}
                />
              </ListItem>
                  <Divider component="li" sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                  
                  {originalCard.updatedAt && originalCard.updatedAt !== originalCard.createdAt && (
                    <>
                      <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <DescriptionIcon color="info" />
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography fontWeight="500">Cartão atualizado</Typography>}
                          secondary={`Última atualização em ${formatDate(originalCard.updatedAt)}`}
                        />
                      </ListItem>
                      <Divider component="li" sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    </>
                  )}
                  
                  {originalCard.dueDate && (
                    <>
                      <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <AccessTimeIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography fontWeight="500">Data de entrega definida</Typography>}
                          secondary={`Prazo em ${formatDateNoHours(originalCard.dueDate)}`}
                          secondaryTypographyProps={{ sx: { color: isDatePast(new Date(originalCard.dueDate)) && status !== Status.DONE ? '#f44336' : 'inherit' } }}
                        />
                      </ListItem>
                      <Divider component="li" sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    </>
                  )}
                  
                  {originalCard.assignees && originalCard.assignees.length > 0 && (
                    <>
                      <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <PersonIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography fontWeight="500">
                              {originalCard.assignees.length === 1 ? "Responsável atribuído" : "Responsáveis atribuídos"}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              {originalCard.assignees.map((assignee) => (
                                <Box key={assignee.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                  <Avatar src={assignee.avatar} sx={{ width: 24, height: 24, mr: 1 }}>
                                    {assignee.name.charAt(0)}
                                  </Avatar>
                                  <Typography variant="body2">{assignee.name}</Typography>
                                </Box>
                              ))}
                            </Box>
                          }
                        />
                      </ListItem>
                      <Divider component="li" sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    </>
                  )}
                  
                  {comments && comments.length > 0 && (
                    <>
                      <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <InsertCommentIcon color="info" />
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography fontWeight="500">Comentários</Typography>}
                          secondary={`${comments.length} comentário${comments.length !== 1 ? 's' : ''} neste cartão`}
                        />
                      </ListItem>
                      <Divider component="li" sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                    </>
                  )}
                  
                  {attachments && attachments.length > 0 && (
                    <>
                      <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <AttachmentIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography fontWeight="500">Anexos</Typography>}
                          secondary={`${attachments.length} arquivo${attachments.length !== 1 ? 's' : ''} anexado${attachments.length !== 1 ? 's' : ''} ao cartão`}
                        />
                      </ListItem>
                    </>
                  )}
                </>
              )}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!isObserver && canDeleteCard && (
          <DeleteButton
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            onClick={handleDeleteCardClick}
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir Cartão'}
          </DeleteButton>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <CancelButton onClick={onClose}>
          {isObserver ? 'Fechar' : 'Cancelar'}
        </CancelButton>
        {!isObserver && (
          <SaveButton
            variant="contained"
            disabled={!hasChanges || isSaving}
            onClick={handleSaveCard}
          >
            {isSaving ? <CircularProgress size={24} color="inherit" /> : "Salvar Alterações"}
          </SaveButton>
        )}
      </DialogActions>
    </StyledDialog>
    
    <Dialog
      open={deleteCardConfirmOpen}
      onClose={handleDeleteCardConfirmClose}
      PaperProps={{
        style: {
          backgroundColor: '#3B4658',
          borderRadius: '4px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
          maxWidth: '500px',
          padding: 0
        }
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
          Excluir cartão
        </Typography>
        
        <Typography sx={{ color: 'white', mb: 3 }}>
          Tem certeza que deseja excluir o cartão "{title}"? Esta ação não pode ser desfeita 
          e todos os anexos e comentários associados a este cartão serão perdidos permanentemente.
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleDeleteCardConfirmClose} sx={{ color: '#2196f3', textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button onClick={handleDeleteCard} sx={{ color: '#f44336', textTransform: 'none', ml: 1 }}>
            Excluir
          </Button>
        </Box>
      </Box>
    </Dialog>
  </>
  );
};

export default OpenCardModal;